<template>
  <Dialog
    ref="dialog"
    :dismissable="false"
    trigger-classes="flex justify-center sm:justify-start focus:outline-none"
    dialog-classes="!p-6 dialog-wrapper"
    :on-open="onOpen"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>
    <template v-slot:content="{ closeDialog }">
      <div class="flex flex-col" :class="isNewPhotoEditor ? 'gap-y-4' : 'gap-y-6'">
        <template v-if="!isNewPhotoEditor">
          <template v-if="slots.content">
            <slot name="content"></slot>
          </template>
          <template v-else>
            <p class="mb-0">
              <strong class="font-body text-xl font-weight-500 text-typo">
                {{ I18n.t("improve.thanks_for_being_honest") }}{{ " " }}
              </strong>
            </p>
            <p class="mb-0 font-body text-base text-typo">{{ I18n.t("improve.help_further_improve_ai") }}</p>
          </template>
        </template>
        <template v-else>
          <p class="mb-0">
            <strong class="font-body text-lg font-weight-500 text-typo">{{ I18n.t("improve.report_result") }}</strong>
          </p>
          <p class="mb-0 font-body text-base text-typo-secondary">{{ I18n.t("improve.submit_to_improve") }}</p>
        </template>

        <div class="mb-0">
          <p class="!mb-3">
            <Checkbox :v-model="consented" :error="hasConsentError" @update:modelValue="handleConsentChange">
              <span v-html="I18n.t('improve.agree_to_x', { x: ipc_url })"> </span>
            </Checkbox>
          </p>
          <p>
            <Checkbox :v-model="rememberDecision" @update:modelValue="(newValue) => (rememberDecision = newValue)">
              {{ rememberChoiceText }}
            </Checkbox>
          </p>
        </div>

        <div class="sm:flex sm:flex-row-reverse sm:gap-x-3 space-y-3 sm:space-y-0 grow">
          <Button class="w-full md:w-auto" autofocus type="button" :disabled="!consented" :on-click="onSubmit">{{
            I18n.t("improve.submit_image")
          }}</Button>
          <Button
            class="!bg-transparent hover:!bg-transparent active:!bg-transparent text-primary hover:!text-primary-hover text-base w-full md:w-auto"
            type="reset"
            :on-click="handleCancel"
            >{{ I18n.t("editor.cancel") }}</Button
          >
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { I18n as I18nType } from "@/src/i18n";
import { Dialog, Button, Checkbox } from "prism";
import { onMounted, ref, watch, inject, computed, useSlots } from "vue";
import Routes from "@/modules/routes";
import { Image, ProcessingState } from "@/modules/internal_api/image";
import {
  rbgImprovementProgramConsentPanelOpenedV100,
  rbgImprovementProgramConsentPanelSubmittedV100,
} from "kaleido-event-tracker";
import { usePhotoEditor } from "@/composables/photo_editor";
import { useImageReporter } from "@/composables/image_reporter";

const I18n = inject<I18nType>("I18n");
const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();
const slots = useSlots();

interface QIPDialogProps {
  image: Image;
  triggerClass: string;
}

const props = defineProps<QIPDialogProps>();

const hasConsentError = ref<Boolean>(false);
const rememberDecision = ref<Boolean>(false);

const isRated = ref<Boolean>(false);
const rating = ref<Number>(0);
const dialog = ref(null);

const { consented, rateNegative, saveConsentedDecision } = useImageReporter();

onMounted(() => {
  refresh();
});

watch(
  () => props.image,
  () => refresh()
);

const emit = defineEmits<{
  (e: "onCancel"): void;
  (e: "onSubmit"): void;
}>();

const onOpen = () => {
  rbgImprovementProgramConsentPanelOpenedV100({ image_id: props.image.meta.id });
};

const handleCancel = () => {
  emit("onCancel");
  closeDialog();
};

const onSubmit = async () => {
  rbgImprovementProgramConsentPanelSubmittedV100({
    image_id: props.image.meta.id,
    remember_decision: rememberDecision.value as boolean,
  });

  if (rememberDecision.value) {
    saveConsentedDecision(consented.value);
  }

  await contribute();
  if (!hasConsentError.value) {
    emit("onSubmit");
    closeDialog();
  }
};

const rememberChoiceText = computed(() => {
  return isNewPhotoEditor.value ? I18n.t("improve.remember_choice") : I18n.t("improve.remember");
});

const closeDialog = () => {
  dialog.value?.closeDialog();
};

const refresh = () => {
  if (props.image?.previewResult?.state === ProcessingState.Finished) {
    isRated.value = props.image.previewResult.rated;
    rating.value = props.image.previewResult.rating;
  } else {
    isRated.value = false;
    rating.value = 0;
  }
};

const handleConsentChange = (newValue) => {
  hasConsentError.value = false;
  consented.value = newValue;
};

const contribute = async () => {
  if (!consented.value) {
    hasConsentError.value = true;
    return;
  }

  rateNegative(props.image);
  closeDialog();
};

const ipc_url = computed(() => {
  return `<a href="${Routes.get("ipc_url")}" target="_blank">${I18n.t("improve.ipc")}</a>`;
});

const showDialog = () => {
  dialog.value.showDialog();
};

defineExpose({
  showDialog,
});
</script>
